import LegalLayout, {
  LegalSection,
  LegalSubheading,
  LegalList,
} from '../components/LegalLayout';
import { BRAND } from '../lib/brand';

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      intro={`This Privacy Policy explains how ${BRAND.legalName} ("${BRAND.name}", "we", "us", or "our") collects, uses, discloses, and safeguards your information when you visit ${BRAND.domain}, sign up for an account, or use our services.`}
    >
      <LegalSection heading="1. Information We Collect">
        <LegalSubheading>1.1 Information you provide</LegalSubheading>
        <p>
          When you create an account, contact us, or subscribe to a plan, we
          collect information such as your name, email address, business name,
          billing details, and any content you submit to the service (campaign
          briefs, creative assets, ad copy, etc.).
        </p>

        <LegalSubheading>1.2 Information collected automatically</LegalSubheading>
        <p>
          We automatically collect technical information when you use our
          services, including IP address, device identifiers, browser type and
          version, operating system, referring URLs, pages viewed, and
          timestamps. This is collected through cookies and similar tracking
          technologies. See Section 9 for details.
        </p>

        <LegalSubheading>1.3 Information from third parties</LegalSubheading>
        <p>
          If you connect a third-party account (e.g., Meta, Google, TikTok,
          LinkedIn), we receive profile and analytics data from those platforms
          in accordance with the permissions you grant. We also receive
          information from payment processors and identity providers (such as
          OAuth/SSO providers).
        </p>
      </LegalSection>

      <LegalSection heading="2. How We Use Your Information">
        <p>We use the information we collect to:</p>
        <LegalList
          items={[
            'Provide, operate, maintain, and improve our services.',
            'Process payments, send invoices, and manage subscriptions.',
            'Communicate with you about your account, updates, security alerts, and support requests.',
            'Personalize content, features, and recommendations.',
            'Monitor and analyze usage and trends to improve user experience.',
            'Detect, investigate, and prevent fraudulent transactions and other illegal activities.',
            'Comply with legal obligations and enforce our agreements.',
            'With your consent, send marketing communications (you can opt out at any time).',
          ]}
        />
      </LegalSection>

      <LegalSection heading="3. Legal Bases for Processing (EEA / UK / Switzerland)">
        <p>
          If you are in the European Economic Area, the United Kingdom, or
          Switzerland, we process personal data on the following legal bases:
        </p>
        <LegalList
          items={[
            <>
              <strong className="text-foreground">Contract</strong> — to provide
              the services you have requested.
            </>,
            <>
              <strong className="text-foreground">Legitimate interests</strong>{' '}
              — to operate, secure, and improve our services, and to communicate
              with customers.
            </>,
            <>
              <strong className="text-foreground">Consent</strong> — for
              optional cookies and marketing communications (which you may
              withdraw at any time).
            </>,
            <>
              <strong className="text-foreground">Legal obligation</strong> — to
              comply with applicable laws and regulations.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection heading="4. How We Share Information">
        <p>
          We do not sell or rent your personal information. We share information
          only as described below:
        </p>
        <LegalList
          items={[
            'Service providers acting on our behalf (e.g., hosting, analytics, payment processing, email delivery) bound by appropriate confidentiality and data-protection obligations.',
            'Third-party platforms you choose to integrate with (e.g., to run ad campaigns on Meta or Google).',
            'Professional advisors such as auditors and lawyers, under duties of confidentiality.',
            'Law enforcement or government authorities when required by law, subpoena, or to protect the rights, property, or safety of us or others.',
            'In connection with a merger, acquisition, financing, or sale of business assets — with notice to affected users.',
          ]}
        />
      </LegalSection>

      <LegalSection heading="5. International Data Transfers">
        <p>
          We are based in the United States and operate globally. Personal data
          we collect may be transferred to, stored in, and processed in any
          country in which we or our service providers operate. Where required,
          we rely on appropriate safeguards such as the European Commission's
          Standard Contractual Clauses (SCCs) for international transfers.
        </p>
      </LegalSection>

      <LegalSection heading="6. Data Retention">
        <p>
          We retain personal information for as long as your account is active
          or as needed to provide our services, comply with our legal
          obligations, resolve disputes, and enforce our agreements. When data
          is no longer needed, we securely delete or anonymize it.
        </p>
      </LegalSection>

      <LegalSection heading="7. Your Rights">
        <p>
          Depending on your jurisdiction (e.g., GDPR, UK GDPR, CCPA/CPRA), you
          may have the following rights regarding your personal data:
        </p>
        <LegalList
          items={[
            'Access — request a copy of the personal data we hold about you.',
            'Correction — request that inaccurate or incomplete data be updated.',
            'Deletion — request deletion of your personal data, subject to legal exceptions.',
            'Portability — receive your data in a structured, machine-readable format.',
            'Restriction or objection — restrict or object to certain processing.',
            'Withdrawal of consent — where processing is based on consent.',
            'Opt-out of "sale" or "sharing" of personal information (CCPA/CPRA). We do not sell personal information as defined under applicable law.',
            'Right to lodge a complaint with your local data-protection authority.',
          ]}
        />
        <p>
          To exercise any of these rights, contact us at{' '}
          <a
            className="text-foreground underline underline-offset-4 hover:opacity-80"
            href={`mailto:${BRAND.email}`}
          >
            {BRAND.email}
          </a>
          . We will respond within the timeframe required by law.
        </p>
      </LegalSection>

      <LegalSection heading="8. Children's Privacy">
        <p>
          Our services are not directed to individuals under the age of 16, and
          we do not knowingly collect personal information from children. If we
          learn we have collected personal information from a child without
          verified parental consent, we will delete it promptly.
        </p>
      </LegalSection>

      <LegalSection heading="9. Cookies and Tracking Technologies">
        <p>
          We use cookies and similar technologies to recognize your browser,
          remember your preferences, analyze how the service is used, and
          deliver relevant marketing. We use the following categories:
        </p>
        <LegalList
          items={[
            <>
              <strong className="text-foreground">Strictly necessary</strong> —
              required for the service to function (e.g., authentication,
              security).
            </>,
            <>
              <strong className="text-foreground">Analytics</strong> — help us
              understand how visitors interact with the service.
            </>,
            <>
              <strong className="text-foreground">Marketing</strong> — used to
              deliver relevant content and measure campaign performance.
            </>,
          ]}
        />
        <p>
          You can manage non-essential cookies through your browser settings or
          our cookie preferences (where available). Disabling certain cookies
          may affect functionality.
        </p>
      </LegalSection>

      <LegalSection heading="10. Security">
        <p>
          We implement administrative, technical, and physical safeguards
          designed to protect personal information, including encryption in
          transit (TLS), encryption at rest where applicable, access controls,
          and regular security reviews. No system is 100% secure, and we cannot
          guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection heading="11. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. When we do, we
          will revise the "Last updated" date at the top of this page. Material
          changes will be communicated through the service or by email. Your
          continued use of the service after the changes take effect constitutes
          acceptance of the updated policy.
        </p>
      </LegalSection>

      <LegalSection heading="12. Contact Us">
        <p>
          If you have questions about this Privacy Policy or how we handle your
          information, please contact us:
        </p>
        <div className="mt-4 rounded-xl border border-border bg-card p-5 text-foreground/85">
          <p>
            <strong className="text-foreground">{BRAND.legalName}</strong>
          </p>
          <p>{BRAND.address}</p>
          <p className="mt-2">
            Email:{' '}
            <a
              className="text-foreground underline underline-offset-4 hover:opacity-80"
              href={`mailto:${BRAND.email}`}
            >
              {BRAND.email}
            </a>
          </p>
        </div>
      </LegalSection>
    </LegalLayout>
  );
}
